import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useEventListener } from "usehooks-ts";

export type Dimensions = {
  width: number;
  height?: number;
};

type Limits = {
  minWidth: number;
  maxWidth?: number;
};

export type ResizeHandler = (size: Dimensions) => void;

export interface ResizeOptions {
  defaultWidth?: number;
  onResize?: ResizeHandler;
  onHandleClick?: React.MouseEventHandler;
  isResizable?: boolean;
  handlePosition?: "right" | "left";
}

export type UseResizeProps = ResizeOptions;

export type UseResizeReturn = ReturnType<typeof useResize>;

export interface ResizeProviderContext extends UseResizeReturn {
  isResizable: boolean;
  handlePosition: UseResizeProps["handlePosition"];
}

export const ResizeContext = createContext<ResizeProviderContext>({
  isResizable: true,
  handlePosition: "right",
  getContainerProps: () => ({}),
  getHandleProps: () => ({}),
  width: 280,
});

export const useResizeContext = () => useContext(ResizeContext);

/**
 * Hook used to create horizontally resizable elements.
 *
 * Automatically detects min/max width from CSS properties.
 */
export const useResize = (props: UseResizeProps = {}) => {
  const {
    defaultWidth = 280,
    onResize,
    onHandleClick,
    isResizable = true,
    handlePosition = "right",
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<any>();
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(defaultWidth);

  const dimensionsRef = useRef<Dimensions>({ width });
  const limitsRef = useRef<Limits>({ minWidth: 0, maxWidth: undefined });

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    onResize?.(dimensionsRef.current);
  }, []);

  const resize = useCallback(
    (event: MouseEvent) => {
      if (!isResizing || !containerRef.current) {
        return;
      }

      const r = containerRef.current.getBoundingClientRect();
      const { minWidth, maxWidth } = limitsRef.current;

      let w;
      if (handlePosition === "right") {
        w = event.clientX - (r?.left || 0);
      } else {
        w = r.right - event.clientX;
      }

      if (w < r.width && minWidth) {
        w = Math.max(w, minWidth);
      } else if (w > r.width && maxWidth) {
        w = Math.min(w, maxWidth);
      }
      setWidth(w);

      dimensionsRef.current = { width: r.width, height: r.height };
    },
    [isResizing],
  );

  useEffect(() => {
    if (!containerRef.current || typeof getComputedStyle === "undefined") {
      return;
    }
    const styles = getComputedStyle(containerRef.current);
    limitsRef.current = {
      minWidth: parseInt(styles.getPropertyValue("min-width")),
      maxWidth: parseInt(styles.getPropertyValue("max-width")) || undefined,
    };
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", resize);
    }

    return () => {
      document.removeEventListener("mousemove", resize);
    };
  }, [isResizing]);

  useEventListener("mouseup", stopResizing);

  const getContainerProps = useCallback(
    (props: React.HTMLAttributes<HTMLDivElement> = {}) => ({
      ref: containerRef,
      ...(isResizable
        ? {
            style: {
              width,
            } as React.CSSProperties,
          }
        : {}),
      ...props,
    }),
    [width, isResizable, containerRef],
  );

  const getHandleProps = useCallback(
    () => ({
      onMouseDown: () => {
        timeoutRef.current = setTimeout(() => setIsResizing(true), 100);
      },
      onMouseUp: (e: any) => {
        if (!isResizing) {
          clearTimeout(timeoutRef.current);
          setIsResizing(false);
          onHandleClick?.(e);
        }
      },
      // 'data-resizing': dataAttr(isResizing),
      [handlePosition]: "-5px",
    }),
    [handlePosition, isResizing],
  );

  return {
    getContainerProps,
    getHandleProps,
    width,
  };
};
